import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ScanLine, Trash2, Edit3 } from "lucide-react";

const emptyGuest = {
  first_name: "", last_name: "", document_type: "carta_identita",
  document_number: "", nationality: "ITA", birth_date: "", birth_place: "",
  gender: "M", issue_date: "", expiry_date: "", issuing_authority: "",
  email: "", phone: "", language: "it",
  tax_exempt: false, tax_exempt_reason: "",
};

export default function Guests() {
  const [guests, setGuests] = useState([]);
  const [form, setForm] = useState(emptyGuest);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(() => api.get("/guests").then(r => setGuests(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v === "" ? null : v]));
      if (editingId) await api.put(`/guests/${editingId}`, payload);
      else await api.post("/guests", payload);
      toast.success("Salvato");
      setForm(emptyGuest); setEditingId(null); setShowForm(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const onEdit = (g) => {
    const filled = Object.fromEntries(Object.entries(emptyGuest).map(([k]) => [k, g[k] ?? emptyGuest[k]]));
    setForm(filled); setEditingId(g.id); setShowForm(true);
  };

  const onDelete = async (id) => {
    if (!confirm("Eliminare l'ospite?")) return;
    await api.delete(`/guests/${id}`); toast.success("Eliminato"); load();
  };

  return (
    <div className="p-6 md:p-12 max-w-7xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-xs tracked text-blue-700 mb-2">ANAGRAFICA</p>
          <h1 className="font-heading font-semibold text-4xl tracking-tight">Ospiti</h1>
        </div>
        <div className="flex gap-3">
          <Link to="/app/scanner" data-testid="goto-scanner"
            className="inline-flex items-center gap-2 bg-blue-700 text-stone-50 px-4 py-2.5 text-sm rounded-lg hover:bg-blue-700 transition-colors">
            <ScanLine className="w-4 h-4" /> Scansiona documento
          </Link>
          <button onClick={() => { setShowForm(true); setEditingId(null); setForm(emptyGuest); }}
            data-testid="add-guest"
            className="bg-stone-900 text-stone-50 px-4 py-2.5 text-sm rounded-lg hover:bg-stone-800 transition-colors">
            + Aggiungi
          </button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={onSubmit} className="border border-slate-200 bg-white p-6 mb-8 grid md:grid-cols-3 gap-4" data-testid="guest-form">
          <Field label="Nome"><input required value={form.first_name} onChange={set("first_name")} className={inputCls} /></Field>
          <Field label="Cognome"><input required value={form.last_name} onChange={set("last_name")} className={inputCls} /></Field>
          <Field label="Sesso">
            <select value={form.gender} onChange={set("gender")} className={inputCls}>
              <option value="M">M</option><option value="F">F</option>
            </select>
          </Field>
          <Field label="Tipo documento">
            <select value={form.document_type} onChange={set("document_type")} className={inputCls}>
              <option value="carta_identita">Carta d'identità</option>
              <option value="passaporto">Passaporto</option>
              <option value="patente">Patente</option>
            </select>
          </Field>
          <Field label="Numero documento"><input required value={form.document_number} onChange={set("document_number")} className={inputCls} /></Field>
          <Field label="Nazionalità (ISO 3)"><input value={form.nationality} onChange={set("nationality")} className={inputCls} /></Field>
          <Field label="Data nascita"><input type="date" value={form.birth_date || ""} onChange={set("birth_date")} className={inputCls} /></Field>
          <Field label="Luogo nascita"><input value={form.birth_place} onChange={set("birth_place")} className={inputCls} /></Field>
          <Field label="Autorità emittente"><input value={form.issuing_authority} onChange={set("issuing_authority")} className={inputCls} /></Field>
          <Field label="Data emissione"><input type="date" value={form.issue_date || ""} onChange={set("issue_date")} className={inputCls} /></Field>
          <Field label="Data scadenza"><input type="date" value={form.expiry_date || ""} onChange={set("expiry_date")} className={inputCls} /></Field>
          <Field label="Email"><input type="email" value={form.email} onChange={set("email")} className={inputCls} /></Field>
          <Field label="Telefono"><input value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+39..." /></Field>
          <Field label="Lingua preferita (email/SMS)">
            <select value={form.language} onChange={set("language")} className={inputCls} data-testid="guest-language">
              <option value="it">Italiano</option>
              <option value="en">English</option>
              <option value="fr">Français</option>
              <option value="de">Deutsch</option>
              <option value="zh">中文</option>
            </select>
          </Field>

          <label className="flex items-center gap-2 mt-7 text-sm">
            <input type="checkbox" checked={form.tax_exempt} onChange={(e) => setForm(f => ({ ...f, tax_exempt: e.target.checked }))} className="accent-blue-700" data-testid="guest-tax-exempt" />
            <span>Esente tassa di soggiorno</span>
          </label>
          {form.tax_exempt && (
            <div className="md:col-span-3">
              <Field label="Motivo esenzione">
                <select value={form.tax_exempt_reason} onChange={set("tax_exempt_reason")} className={inputCls}>
                  <option value="">— seleziona —</option>
                  <option value="disabile">Disabile / invalido</option>
                  <option value="lunga_permanenza">Lunga permanenza</option>
                  <option value="autista">Autista / accompagnatore</option>
                  <option value="forze_ordine">Forze dell'ordine in servizio</option>
                  <option value="altro">Altro</option>
                </select>
              </Field>
            </div>
          )}

          <div className="md:col-span-3 flex gap-2 mt-2">
            <button data-testid="guest-submit" className="bg-stone-900 text-stone-50 px-5 py-2.5 text-sm rounded-lg hover:bg-stone-800">Salva</button>
            <button type="button" onClick={() => { setShowForm(false); setEditingId(null); setForm(emptyGuest); }} className="text-slate-600 px-4 py-2 text-sm">Annulla</button>
          </div>
        </form>
      )}

      <div className="border border-slate-200 bg-white">
        <div className="p-6 border-b border-slate-200 text-xs tracked text-slate-500">{guests.length} OSPITI</div>
        {guests.length === 0 ? (
          <div className="p-12 text-center text-slate-500 text-sm">Nessun ospite registrato.</div>
        ) : (
          <table className="w-full text-sm" data-testid="guests-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left p-4">Nome</th>
                <th className="text-left p-4">Documento</th>
                <th className="text-left p-4">Nazionalità</th>
                <th className="text-left p-4">Contatti</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {guests.map(g => (
                <tr key={g.id} className="border-t border-slate-200 hover:bg-slate-50">
                  <td className="p-4 font-medium">{g.first_name} {g.last_name}</td>
                  <td className="p-4 text-slate-600">{g.document_type} · {g.document_number}</td>
                  <td className="p-4 text-slate-600">{g.nationality}</td>
                  <td className="p-4 text-slate-600 text-xs">{g.email || "—"}<br />{g.phone || ""}</td>
                  <td className="p-4 text-right">
                    <button onClick={() => onEdit(g)} className="text-slate-500 hover:text-blue-700 mr-3"><Edit3 className="w-4 h-4" /></button>
                    <button onClick={() => onDelete(g.id)} className="text-slate-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const inputCls = "w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-blue-700 bg-white";
function Field({ label, children }) {
  return (<label className="block"><span className="text-xs text-slate-500">{label}</span><div className="mt-1">{children}</div></label>);
}
